import React, { useState, useEffect, useRef, useCallback } from "react";
import styles from "./maintenancerequest.module.css";
import Loader from "../../components/Loader";
import { useAuth0 } from "@auth0/auth0-react";
import Footer from "../../components/Footer";
import { Autocomplete, Dialog, DialogContent, DialogTitle, IconButton, Slide, Snackbar, TextField } from "@mui/material";
import { ArrowLeft, ArrowRight, ArrowUp, ChevronDown, ChevronUp, Code, Filter, Home, Plus, Search, X } from "react-feather";
import dayjs from "dayjs";
import { Dropdown } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { Helmet } from "react-helmet";
import axios from "axios";
import { toast } from "react-smart-toaster";
import { TableLoader } from "../../components/LoaderC";
import relativeTime from 'dayjs/plugin/relativeTime';
import { AddLogCallAPI } from "../../components/AddLogs";
import AddMaintenanceForm from "./components/AddMaintenanceForm";
import withCustomAuthenticationRequired from "../../auth/withCustomAuthenticationRequired";
import MaintenanceAnalytics from "./components/analytics/MaintenanceAnalytics";

dayjs.extend(relativeTime);

const CHUNK_SIZE = 20;

const SUB_CATEGORY_IMAGES = {
  'Electrical Socket': '/static/img/ElectricalSocket.svg',
  'Light Switch/Dimmer': '/static/img/Dimmer.svg',
  'Mount Television': '/static/img/tv.svg',
  'Leak': '/static/img/Leak.svg',
  'Drain Clogged': '/static/img/DrainClogged.svg',
  'Water Temperature Issue': '/static/img/WaterTemperature.svg',
  'Heating & Cooling System': '/static/img/heat&cool.svg',
  'Stove/Oven Issue': '/static/img/StoveOven.svg',
  'Refrigerator Issue': '/static/img/Refrigerator.svg',
  'Washer/Dryer Issue': '/static/img/Washer.svg',
  'Door Lock': '/static/img/Door.svg',
  'Dimmer': '/static/img/bulb.svg',
  'Thermostat': '/static/img/temperature.svg',
  'Other Issue': '/static/img/other.svg',
};

const categoryGroupOptions = [
  { label: "Electrical", parent: 1 },
  { value: "Mount Television", label: "Mount Television", parent: 0, category: "Electrical", img_path: "/static/img/tv.svg" },
  { value: "Electrical Socket", label: "Electrical Socket", parent: 0, category: "Electrical", img_path: "/static/img/ElectricalSocket.svg" },
  { value: "Light Switch/Dimmer", label: "Light Switch/Dimmer", parent: 0, category: "Electrical", img_path: "/static/img/Dimmer.svg" },
  { label: "Plumbing", parent: 1 },
  { value: "Leak", label: "Leak", parent: 0, category: "Plumbing", img_path: "/static/img/Leak.svg" },
  { value: "Drain Clogged", label: "Drain Clogged", parent: 0, category: "Plumbing", img_path: "/static/img/DrainClogged.svg" },
  { value: "Water Temperature Issue", label: "Water Temperature Issue", parent: 0, category: "Plumbing", img_path: "/static/img/WaterTemperature.svg" },
  { value: "Heating & Cooling System", label: "Heating & Cooling System", parent: 0, category: "Plumbing", img_path: "/static/img/heat&cool.svg" },
  { label: "Appliances", parent: 1 },
  { value: "Stove/Oven Issue", label: "Stove/Oven Issue", parent: 0, category: "Appliances", img_path: "/static/img/StoveOven.svg" },
  { value: "Refrigerator Issue", label: "Refrigerator Issue", parent: 0, category: "Appliances", img_path: "/static/img/Refrigerator.svg" },
  { value: "Washer/Dryer Issue", label: "Washer/Dryer Issue", parent: 0, category: "Appliances", img_path: "/static/img/Washer.svg" },
  { label: "Smart Devices", parent: 1 },
  { value: "Door Lock", label: "Door Lock", parent: 0, category: "Smart Devices", img_path: "/static/img/Door.svg" },
  { value: "Dimmer", label: "Dimmer", parent: 0, category: "Smart Devices", img_path: "/static/img/bulb.svg" },
  { value: "Thermostat", label: "Thermostat", parent: 0, category: "Smart Devices", img_path: "/static/img/temperature.svg" },
  { label: "Other", parent: 1 },
  { value: "Other Issue", label: "Other Issue", parent: 0, category: "Other", img_path: "/static/img/other.svg" },
];

// Static option lists for the now-Autocomplete filters (Status, Priority, Sort by Date)
const STATUS_OPTIONS = [
  { value: "Open", label: "Open" },
  { value: "Assigned", label: "Assigned" },
  { value: "Scheduled", label: "Scheduled" },
  { value: "Pending Followup", label: "Pending Follow-up" },
  { value: "Resolved", label: "Resolved" },
  { value: "Hold", label: "Hold" },
];

const PRIORITY_OPTIONS = [
  { value: "Low", label: "Low" },
  { value: "Medium", label: "Medium" },
  { value: "High", label: "High" },
];

const SORT_DATE_OPTIONS = [
  { value: "latest", label: "Latest - Earliest" },
  { value: "erliest", label: "Earliest - Latest" },
];

const getImageForSubCategory = (subCategory) => SUB_CATEGORY_IMAGES[subCategory] || '/static/img/other.svg';

const OpenedToggleReadOnly = React.forwardRef(({ children, onClick }, ref) => (
  <button ref={ref} className={`${styles.ReferStatusBU} ${styles.ReferStatusBUNew} ${styles.Open}`} onClick={(e) => { e.preventDefault(); onClick(e); }}>
    Open
  </button>
));

const PendingToggleReadOnly = React.forwardRef(({ children, onClick }, ref) => (
  <button ref={ref} className={`${styles.ReferStatusBU} ${styles.ReferStatusBUNew} ${styles.FollowUp}`} onClick={(e) => { e.preventDefault(); onClick(e); }}>
    Pending Follow-up
  </button>
));

const ResolvedToggleReadOnly = React.forwardRef(({ children, onClick }, ref) => (
  <button ref={ref} className={`${styles.ReferStatusBU} ${styles.ReferStatusBUNew} ${styles.Resolved}`} onClick={(e) => { e.preventDefault(); onClick(e); }}>
    Resolved
  </button>
));

const AssignedToggleReadOnly = React.forwardRef(({ children, onClick }, ref) => (
  <button ref={ref} className={`${styles.ReferStatusBU} ${styles.ReferStatusBUNew} ${styles.InProgress}`} onClick={(e) => { e.preventDefault(); onClick(e); }}>
    Assigned
  </button>
));

const ScheduledOverdueToggleReadOnly = React.forwardRef(({ children, onClick }, ref) => (
  <button ref={ref} className={`${styles.ReferStatusBU} ${styles.ReferStatusBUNew} ${styles.ScheduledOverdue}`} onClick={(e) => { e.preventDefault(); onClick(e); }}>
    Overdue
  </button>
));

const ScheduledToggleReadOnly = React.forwardRef(({ children, onClick }, ref) => (
  <button ref={ref} className={`${styles.ReferStatusBU} ${styles.ReferStatusBUNew} ${styles.Scheduled}`} onClick={(e) => { e.preventDefault(); onClick(e); }}>
    Scheduled
  </button>
));

const CancelledToggleReadOnly = React.forwardRef(({ children, onClick }, ref) => (
  <button ref={ref} className={`${styles.ReferStatusBU} ${styles.ReferStatusBUNew} ${styles.Cancelled}`} onClick={(e) => { e.preventDefault(); onClick(e); }}>
    Hold
  </button>
));

const OpenedToggle = React.forwardRef(({ children, onClick }, ref) => (
  <button ref={ref} className={`${styles.ReferStatusBU} ${styles.Open}`} onClick={(e) => { e.preventDefault(); onClick(e); }}>
    Open <ChevronDown className={`${styles.DownArrowStatus}`} />
  </button>
));

const PendingToggle = React.forwardRef(({ children, onClick }, ref) => (
  <button ref={ref} className={`${styles.ReferStatusBU} ${styles.FollowUp}`} onClick={(e) => { e.preventDefault(); onClick(e); }}>
    Pending Follow-up <ChevronDown className={`${styles.DownArrowStatus}`} />
  </button>
));

const ResolvedToggle = React.forwardRef(({ children, onClick }, ref) => (
  <button ref={ref} className={`${styles.ReferStatusBU} ${styles.Resolved}`} onClick={(e) => { e.preventDefault(); onClick(e); }}>
    Resolved <ChevronDown className={`${styles.DownArrowStatus}`} />
  </button>
));

const AssignedToggle = React.forwardRef(({ children, onClick }, ref) => (
  <button ref={ref} className={`${styles.ReferStatusBU} ${styles.InProgress}`} onClick={(e) => { e.preventDefault(); onClick(e); }}>
    Assigned <ChevronDown className={`${styles.DownArrowStatus}`} />
  </button>
));

const ScheduledOverdueToggle = React.forwardRef(({ children, onClick }, ref) => (
  <button ref={ref} className={`${styles.ReferStatusBU} ${styles.ScheduledOverdue}`} onClick={(e) => { e.preventDefault(); onClick(e); }}>
    Overdue <ChevronDown className={`${styles.DownArrowStatus}`} />
  </button>
));

const ScheduledToggle = React.forwardRef(({ children, onClick }, ref) => (
  <button ref={ref} className={`${styles.ReferStatusBU} ${styles.Scheduled}`} onClick={(e) => { e.preventDefault(); onClick(e); }}>
    Scheduled <ChevronDown className={`${styles.DownArrowStatus}`} />
  </button>
));

const CancelledToggle = React.forwardRef(({ children, onClick }, ref) => (
  <button ref={ref} className={`${styles.ReferStatusBU} ${styles.Cancelled}`} onClick={(e) => { e.preventDefault(); onClick(e); }}>
    Hold <ChevronDown className={`${styles.DownArrowStatus}`} />
  </button>
));

const LowPriortyToggle = React.forwardRef(({ children, onClick }, ref) => (
  <button ref={ref} className={`${styles.ReferStatusBU} ${styles.Resolved}`} onClick={(e) => { e.preventDefault(); onClick(e); }}>
    <ArrowUp className={`${styles.StatusIcon}`} />
    <span>Low</span>
    <ChevronDown className={`${styles.DownArrowStatus}`} />
  </button>
));

const MediumPriortyToggle = React.forwardRef(({ children, onClick }, ref) => (
  <button ref={ref} className={`${styles.ReferStatusBU} ${styles.InProgress}`} onClick={(e) => { e.preventDefault(); onClick(e); }}>
    <ArrowUp className={`${styles.StatusIcon}`} />
    <span>Medium</span>
    <ChevronDown className={`${styles.DownArrowStatus}`} />
  </button>
));

const HighPriortyToggle = React.forwardRef(({ children, onClick }, ref) => (
  <button ref={ref} className={`${styles.ReferStatusBU} ${styles.Cancelled}`} onClick={(e) => { e.preventDefault(); onClick(e); }}>
    <ArrowUp className={`${styles.StatusIcon}`} />
    <span>High</span>
    <ChevronDown className={`${styles.DownArrowStatus}`} />
  </button>
));

const MaintenanceRequest = () => {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [allProperties, setAllProperties] = useState([]);
  const [allAssignees, setAllAssignees] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterData, setFilterData] = useState({ searchKey: "", property: "", apartment: "", status: "", view: "", priority: "", category: "", assignee: "", created: null });
  const [items, setItems] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [apartmentList, setApartmentList] = useState([]);
  const [sortData, setSortData] = useState("");
  const [currentTechnician, setCurrentTechnician] = useState(0);
  const [sortItem, setSortItem] = useState("created");
  const [sortOrder, setSortOrder] = useState("desc");

  const sentinelRef = useRef(null);
  const requestSeq = useRef(0);
  const hasLoadedOnceRef = useRef(false);

  const changeSortOrder = (item) => {
    if (item === sortItem) {
      setSortOrder(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortOrder("asc");
      setSortItem(item);
    }
  };

  const attachImage = (item) => ({ ...item, image: getImageForSubCategory(item?.sub_category) });

  const fetchRequests = useCallback(async (currentOffset, replace) => {
    const seq = ++requestSeq.current;
    const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

    const isInitialLoad = replace && currentOffset === 0 && !hasLoadedOnceRef.current;
    let activeIndicator = 'loadingMore';

    if (isInitialLoad) {
      activeIndicator = 'contentLoading';
      setContentLoading(true);
    } else if (replace) {
      activeIndicator = 'refetching';
      setRefetching(true);
    } else {
      setLoadingMore(true);
    }

    const params = {
      search: filterData.searchKey || "",
      property: filterData.property || "",
      apartment: filterData.apartment || "",
      status: filterData.status || "",
      priority: filterData.priority || "",
      assignee: filterData.assignee || "",
      category: filterData.category || "",
      sort_item: sortItem,
      sort_order: sortOrder,
      offset: currentOffset,
      limit: CHUNK_SIZE,
    };

    try {
      const response = await axios.get(process.env.REACT_APP_CASE_API_URL + "v1/maintenance-requests-new", {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      // Ignore stale responses if the user changed filters again while this was in flight
      if (seq !== requestSeq.current) return;

      const newChunk = (response.data.data || []).map(attachImage);

      setItems(prev => replace ? newChunk : [...prev, ...newChunk]);
      setOffset(currentOffset + newChunk.length);
      setHasMore(Boolean(response.data?.meta?.has_more));
      hasLoadedOnceRef.current = true;
    } catch (error) {
      if (typeof error.response !== "undefined") toast.error(error.response.data.message);
    } finally {
      if (seq === requestSeq.current) {
        if (activeIndicator === 'contentLoading') setContentLoading(false);
        else if (activeIndicator === 'refetching') setRefetching(false);
        else setLoadingMore(false);
      }
    }
  }, [getAccessTokenSilently, filterData, sortItem, sortOrder]);

  useEffect(() => {
    const fetchAllProperties = async () => {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
      await axios.get(process.env.REACT_APP_PAYMENT_API_URL + "v1/property-list", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }).then((response) => {
        let propertyData = response.data.data;
        //propertyData = [{ value: "all", label: "Any" }].concat(propertyData);

        setAllProperties(propertyData);

        if (localStorage.getItem("propertyFilterData")) {
          let filterPropertyId = localStorage.getItem("propertyFilterData");
          localStorage.removeItem("propertyFilterData");

          let propertyTemp = propertyData.filter((i) => i.value === parseInt(filterPropertyId));
          if (propertyTemp.length) {
            setApartmentList(propertyTemp[0]?.apartments);
          }

          setFilterData(prev => { return { ...prev, property: parseInt(filterPropertyId) } });
        }

        if (localStorage.getItem("apartmentFilterData")) {
          let apartmentFilter = localStorage.getItem("apartmentFilterData");
          localStorage.removeItem("apartmentFilterData");
          apartmentFilter = parseInt(apartmentFilter);

          setFilterData(prev => { return { ...prev, apartment: parseInt(apartmentFilter) } });
        }

        if (localStorage.getItem("statusFilterData")) {
          let filterStatus = localStorage.getItem("statusFilterData");
          localStorage.removeItem("statusFilterData");

          setFilterData(prev => { return { ...prev, status: filterStatus } });
        }
      }).catch((error) => {
        if (typeof error.response !== "undefined")
          toast.error(error.response.data.message);
      });
    };
    fetchAllProperties();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const fetchAllAssignees = async () => {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
      await axios.get(process.env.REACT_APP_CASE_API_URL + "v1/technicians-new", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }).then((response) => {
        let aData = response.data.data;
        //aData = [{ value: "all", label: "Any" }].concat(aData);

        setAllAssignees(aData);
      }).catch((error) => {
        if (typeof error.response !== "undefined")
          toast.error(error.response.data.message);
      });
    };
    fetchAllAssignees();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (isAuthenticated) {
      let techTemp = allAssignees?.filter(i => i?.user_id === user['sub']);
      if (techTemp.length > 0) {
        setCurrentTechnician(techTemp[0]?.value);
      }
    }
  }, [allAssignees, isAuthenticated, user]);

  // Refetch from scratch whenever filters or sort change. Search is debounced;
  // everything else (dropdowns, sort headers) refetches immediately.
  useEffect(() => {
    const delay = filterData.searchKey ? 400 : 0;
    const timer = setTimeout(() => {
      fetchRequests(0, true);
    }, delay);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData.searchKey, filterData.property, filterData.apartment, filterData.status, filterData.priority, filterData.assignee, filterData.category, sortItem, sortOrder]);

  // Infinite scroll: load the next chunk when the sentinel row scrolls into view
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loadingMore && !contentLoading && !refetching) {
        fetchRequests(offset, false);
      }
    }, { threshold: 0.1 });

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, contentLoading, offset, fetchRequests, refetching]);

  const changeStatus = async (item, status) => {
    if (item.current_status !== status) {
      setLoading(true);

      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      axios.put(process.env.REACT_APP_CASE_API_URL + "v1/maintenance-requests/" + item?.id, { current_status: status }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }).then((response) => {
        setLoading(false);

        setSnackbarOpen(true);
        setSnackbarMsg(response.data.message);

        setItems((prev) => {
          return prev.map((i) => {
            if (item.id === i.id) {
              return { ...i, current_status: status };
            }
            return i;
          });
        });

        let respData = response.data.data;

        let statusText = "Open";
        if (respData.current_status === 1) {
          statusText = "Assigned";
        }
        if (respData.current_status === 2) {
          statusText = "Scheduled";
        }
        if (respData.current_status === 5) {
          statusText = "Pending Followup";
        }
        if (respData.current_status === 3) {
          statusText = "Resolved";
        }
        if (respData.current_status === 4) {
          statusText = "Hold";
        }
        let logData = {
          title: "The status of the maintenance request has been updated.",
          description: [
            "Name: " + respData.title,
            "Request number: " + respData.request_no,
            "Apartment: " + respData.property_name + ' - ' + respData.apt_name,
            "Tenant: " + respData.tenant_name,
            "Status: " + statusText,
          ],
        };
        AddLogCallAPI(logData, token);
      }).catch((error) => {
        setLoading(false);
        if (typeof error.response !== "undefined")
          toast.error(error.response.data.message);
      });
    }
  }

  const changePriority = async (item, priority) => {
    if (item.priority !== priority) {
      setLoading(true);

      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      axios.put(process.env.REACT_APP_CASE_API_URL + "v1/maintenance-requests/" + item?.id, { priority: priority }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }).then((response) => {
        setLoading(false);

        setSnackbarOpen(true);
        setSnackbarMsg(response.data.message);

        setItems((prev) => {
          return prev.map((i) => {
            if (item.id === i.id) {
              return { ...i, priority: priority };
            }
            return i;
          });
        });

        let respData = response.data.data;

        let statusText = "Low";
        if (respData.priority === 1) {
          statusText = "Low";
        }
        if (respData.priority === 2) {
          statusText = "Medium";
        }
        if (respData.priority === 3) {
          statusText = "High";
        }

        let logData = {
          title: "The priority of the maintenance request has been updated.",
          description: [
            "Name: " + respData.title,
            "Request number: " + respData.request_no,
            "Apartment: " + respData.property_name + ' - ' + respData.apt_name,
            "Tenant: " + respData.tenant_name,
            "Priority: " + statusText,
          ],
        };
        AddLogCallAPI(logData, token);
      }).catch((error) => {
        setLoading(false);
        if (typeof error.response !== "undefined")
          toast.error(error.response.data.message);
      });
    }
  }

  const onSubmitLocal = (data) => {
    const newItem = attachImage(data);
    setItems((prev) => [newItem].concat(prev));
  }

  return (<React.Fragment>
    {loading && <Loader />}
    <Helmet title="Maintenance Request" />

    <div className={`${styles.pageTitleWrap}`}>
      <nav aria-label="breadcrumb">
        <ol className={`${styles.breadcrumb}`}>
          <li className={`${styles.breadcrumbItem}`}><span><Home /></span>Cases</li>
          <li className={`${styles.breadcrumbItem} ${styles.active}`}>Maintenance Request</li>
        </ol>
      </nav>
    </div>

    <div className={`${styles.BodyCon}`}>
      <div className={`${styles.mainCardDiv}`}>
        <div className={`${styles.mainCard}`}>

          {contentLoading && (<div className="Loader TableLoader">
            <TableLoader />
          </div>)}

          {!contentLoading && (<div className="control-pane">
            <div className={`${styles.Row}`}>

              <MaintenanceAnalytics getAccessTokenSilently={getAccessTokenSilently} />

              <div className={(isAuthenticated && user["https://propertydek.com/permissions"].indexOf("request:create") > -1) ? `${styles.SearchFilterSec}` : `${styles.SearchFilterSec} ${styles.Technician}`}>
                <div className={`${styles.SearchCon}`}>
                  <div className={`${styles.SearchConSec}`}>
                    <Search />
                    <input type="text" placeholder="Search" defaultValue={filterData.searchKey} onChange={(e) => {
                      setFilterData((prev) => { return { ...prev, searchKey: e.target.value }; });
                    }} />
                  </div>
                  <button className={`${styles.FilterBu}`} onClick={(e) => setFilterOpen(true)}><Filter /></button>
                  {(isAuthenticated && user["https://propertydek.com/permissions"].indexOf("request:create") > -1) && <button className={`${styles.AddNoteBu}`} onClick={(e) => setAddModalOpen(true)}><Plus /> <span>Maintenance</span></button>}
                </div>

                <div className={`${styles.FilterSec}`}>
                  <div className={filterOpen === true ? `${styles.FilterFieldSec} ${styles.Open}` : `${styles.FilterFieldSec}`}>
                    <p className={`${styles.FilterTitle}`}>
                      Filters
                      <span onClick={(e) => setFilterOpen(false)}><ArrowRight /></span>
                    </p>

                    {/* Property */}
                    <div className={`${styles.FilterCard} ${styles.Select} ${styles.Width200} CusSelectFilter Filter`}>
                      <Autocomplete
                        fullWidth
                        options={allProperties || []}
                        value={allProperties.find(item => item?.value === filterData?.property) || null}
                        getOptionLabel={(option) => option?.label || ""}
                        isOptionEqualToValue={(option, value) => option?.value === value?.value
                        }
                        onChange={(event, newValue) => {
                          const newPropertyValue = newValue?.value || "";
                          setFilterData(prev => ({ ...prev, property: newPropertyValue, apartment: "" }));

                          if (newPropertyValue && newPropertyValue !== "all") {
                            let propertyTemp = allProperties.filter((i) => i.value === newPropertyValue);
                            setApartmentList(propertyTemp[0]?.apartments);
                          } else {
                            setApartmentList([]);
                          }
                        }}
                        renderInput={(params) => (<TextField {...params} label="Property" />)}
                      />
                    </div>

                    {/* Apartment */}
                    <div className={`${styles.FilterCard} ${styles.Select} ${styles.Width200} CusSelectFilter Filter`}>
                      <Autocomplete
                        fullWidth
                        options={apartmentList || []}
                        value={apartmentList.find(item => item?.value === filterData?.apartment) || null}
                        getOptionLabel={(option) => option?.label || ""}
                        isOptionEqualToValue={(option, value) => option?.value === value?.value}
                        noOptionsText={!filterData?.property || filterData?.property === "" ? "Select Property First" : "No options"}
                        onChange={(event, newValue) => {
                          setFilterData(prev => ({ ...prev, apartment: newValue?.value || "" }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Apartment"
                            placeholder={
                              apartmentList.length === 0
                                ? "Select Property First"
                                : "Search apartment"
                            }
                          />
                        )}
                      />
                    </div>

                    {/* Status */}
                    <div className={`${styles.FilterCard} ${styles.Select} ${styles.Width160} CusSelectFilter Filter`}>
                      <Autocomplete
                        fullWidth
                        options={STATUS_OPTIONS}
                        value={
                          STATUS_OPTIONS.find(item => item.value === filterData?.status) || null
                        }
                        getOptionLabel={(option) => option?.label || ""}
                        isOptionEqualToValue={(option, value) =>
                          option?.value === value?.value
                        }
                        onChange={(event, newValue) => {
                          setFilterData(prev => ({ ...prev, status: newValue?.value || "" }));
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Status" />
                        )}
                      />
                    </div>

                    {/* Priority */}
                    <div className={`${styles.FilterCard} ${styles.Select} ${styles.Width160} CusSelectFilter Filter`}>
                      <Autocomplete
                        fullWidth
                        options={PRIORITY_OPTIONS}
                        value={
                          PRIORITY_OPTIONS.find(item => item.value === filterData?.priority) || null
                        }
                        getOptionLabel={(option) => option?.label || ""}
                        isOptionEqualToValue={(option, value) =>
                          option?.value === value?.value
                        }
                        onChange={(event, newValue) => {
                          setFilterData(prev => ({ ...prev, priority: newValue?.value || "" }));
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Priority" />
                        )}
                      />
                    </div>

                    {/* Category */}
                    <div className={`${styles.FilterCard} ${styles.Select} ${styles.Width200} CusSelectFilter Filter`}>
                      <Autocomplete
                        fullWidth
                        options={categoryGroupOptions.filter(item => item?.parent !== 1)}
                        value={
                          categoryGroupOptions.find(
                            item => item?.value === filterData?.category
                          ) || null
                        }
                        groupBy={(option) => option.category}
                        getOptionLabel={(option) => option?.label || ""}
                        isOptionEqualToValue={(option, value) =>
                          option?.value === value?.value
                        }
                        onChange={(event, newValue) => {
                          setFilterData(prev => ({
                            ...prev,
                            category: newValue?.value || ""
                          }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Category"
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option.value}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px"
                              }}
                            >
                              <span>{option.label}</span>
                            </div>
                          </li>
                        )}
                      />
                    </div>

                    {/* View (Technician) / Technician (Admin & others) */}
                    {isAuthenticated && user['https://propertydek.com/roles']?.indexOf('Technician') > -1 ? (
                      <div className={`${styles.FilterCard} ${styles.Select} ${styles.Width180} CusSelectFilter Filter`}>
                        <Autocomplete
                          fullWidth
                          options={[
                            { value: "all", label: "All Requests" },
                            { value: currentTechnician, label: "My Requests" },
                          ]}
                          value={
                            [
                              { value: "all", label: "All Requests" },
                              { value: currentTechnician, label: "My Requests" },
                            ].find(item => item.value === filterData?.assignee) || null
                          }
                          getOptionLabel={(option) => option?.label || ""}
                          isOptionEqualToValue={(option, value) =>
                            option?.value === value?.value
                          }
                          onChange={(event, newValue) => {
                            setFilterData(prev => ({ ...prev, assignee: newValue?.value || "" }));
                          }}
                          renderInput={(params) => (
                            <TextField {...params} label="View" />
                          )}
                        />
                      </div>
                    ) : (
                      <div className={`${styles.FilterCard} ${styles.Select} ${styles.Width180} CusSelectFilter Filter`}>
                        <Autocomplete
                          fullWidth
                          options={allAssignees || []}
                          value={
                            allAssignees.find(item => item?.value === filterData?.assignee) || null
                          }
                          getOptionLabel={(option) => option?.label || ""}
                          isOptionEqualToValue={(option, value) =>
                            option?.value === value?.value
                          }
                          onChange={(event, newValue) => {
                            setFilterData(prev => ({ ...prev, assignee: newValue?.value || "" }));
                          }}
                          renderInput={(params) => (
                            <TextField {...params} label="Technician" />
                          )}
                        />
                      </div>
                    )}

                    {/* Sort by Date */}
                    <div className={`${styles.FilterCard} ${styles.Select} ${styles.Width160} CusSelectFilter Filter`}>
                      <Autocomplete
                        fullWidth
                        options={SORT_DATE_OPTIONS}
                        value={
                          SORT_DATE_OPTIONS.find(item => item.value === sortData) || null
                        }
                        getOptionLabel={(option) => option?.label || ""}
                        isOptionEqualToValue={(option, value) =>
                          option?.value === value?.value
                        }
                        onChange={(event, newValue) => {
                          const val = newValue?.value || "";
                          setSortData(val);
                          if (val === "latest") {
                            setSortItem("created");
                            setSortOrder("desc");
                          } else if (val === "erliest") {
                            setSortItem("created");
                            setSortOrder("asc");
                          }
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Sort by Date" />
                        )}
                      />
                    </div>
                  </div>

                </div>
              </div>

              <div className={`${styles.RequestTableWrap}`}>

                <div style={{ position: 'relative', opacity: refetching ? 0.5 : 1, transition: 'opacity 0.15s ease' }}>

                  {items.length > 0 && (
                    <div className={`${styles.reqTableContainer}`}>
                      <table className={`${styles.requestTable}`}>
                        <thead>
                          <tr>
                            <th onClick={() => changeSortOrder("request")} className={styles.clickableHeader}>
                              <span className={styles.sortableHeader}>
                                Request
                                {sortItem === "request" && sortOrder === "asc" && <ChevronDown />}
                                {sortItem === "request" && sortOrder === "desc" && <ChevronUp />}
                                {sortItem !== "request" && <Code className={styles.Normal} />}
                              </span>
                            </th>
                            <th onClick={() => changeSortOrder("property")} className={styles.clickableHeader}>
                              <span className={styles.sortableHeader}>
                                Property / Unit
                                {sortItem === "property" && sortOrder === "asc" && <ChevronDown />}
                                {sortItem === "property" && sortOrder === "desc" && <ChevronUp />}
                                {sortItem !== "property" && <Code className={styles.Normal} />}
                              </span>
                            </th>
                            <th onClick={() => changeSortOrder("technician")} className={styles.clickableHeader}>
                              <span className={styles.sortableHeader}>
                                Technician
                                {sortItem === "technician" && sortOrder === "asc" && <ChevronDown />}
                                {sortItem === "technician" && sortOrder === "desc" && <ChevronUp />}
                                {sortItem !== "technician" && <Code className={styles.Normal} />}
                              </span>
                            </th>
                            <th onClick={() => changeSortOrder("schedule")} className={styles.clickableHeader}>
                              <span className={styles.sortableHeader}>
                                Schedule
                                {sortItem === "schedule" && sortOrder === "asc" && <ChevronDown />}
                                {sortItem === "schedule" && sortOrder === "desc" && <ChevronUp />}
                                {sortItem !== "schedule" && <Code className={styles.Normal} />}
                              </span>
                            </th>
                            <th onClick={() => changeSortOrder("priority")} className={styles.clickableHeader}>
                              <span className={styles.sortableHeader}>
                                Priority
                                {sortItem === "priority" && sortOrder === "asc" && <ChevronDown />}
                                {sortItem === "priority" && sortOrder === "desc" && <ChevronUp />}
                                {sortItem !== "priority" && <Code className={styles.Normal} />}
                              </span>
                            </th>
                            <th onClick={() => changeSortOrder("status")} className={styles.clickableHeader}>
                              <span className={styles.sortableHeader}>
                                Status
                                {sortItem === "status" && sortOrder === "asc" && <ChevronDown />}
                                {sortItem === "status" && sortOrder === "desc" && <ChevronUp />}
                                {sortItem !== "status" && <Code className={styles.Normal} />}
                              </span>
                            </th>
                            <th onClick={() => changeSortOrder("created")} className={styles.clickableHeader}>
                              <span className={styles.sortableHeader}>
                                Created
                                {sortItem === "created" && sortOrder === "asc" && <ChevronDown />}
                                {sortItem === "created" && sortOrder === "desc" && <ChevronUp />}
                                {sortItem !== "created" && <Code className={styles.Normal} />}
                              </span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item, index) => {
                            const isNearBottom = index >= items.length - 2;
                            const dropdownDrop = isNearBottom ? "up" : "down";

                            return (
                              <tr
                                key={item?.id}
                                className={`${styles.requestRow}`}
                                onClick={() => history.push(`/maintenance/request/details/${item.id}`)}
                              >
                                <td>
                                  <div className={`${styles.reqCell}`}>
                                    <div className={`${styles.reqThumbWrap}`}>
                                      <img src={item?.image} className={`${styles.reqThumb}`} alt="" />
                                    </div>
                                    <div className={`${styles.reqTitleWrap}`}>
                                      <span className={`${styles.reqTitle}`}>{item?.title}</span>
                                      <span className={`${styles.reqSub}`}>{item?.tenant_name} <span className={`${styles.reqSubDot}`}>•</span> {item?.apt_name}</span>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <span className={`${styles.reqPropertyText}`}>{item?.property_name}</span>
                                  <span className={`${styles.reqUnitPill}`}>{item?.apt_name}</span>
                                </td>
                                <td>
                                  {item?.current_status > 0 && item?.technician_name !== '' ? item?.technician_name : '—'}
                                </td>
                                <td>
                                  {item?.current_status > 1 && item?.schedule_date ? (
                                    <div className={`${styles.reqScheduleWrap}`}>
                                      <span>{dayjs(item?.schedule_date).format('MM/DD/YYYY')}</span>
                                      <span className={`${styles.reqScheduleTime}`}>{item?.schedule_time}</span>
                                    </div>
                                  ) : '—'}
                                </td>
                                <td>
                                  {!(item.is_archived === 1) && (isAuthenticated && user["https://propertydek.com/permissions"].indexOf("request:update") > -1) ? (
                                    <div onClick={(e) => e.stopPropagation()}>
                                      <Dropdown drop={dropdownDrop}>
                                        {item.priority === 1 && (<Dropdown.Toggle as={LowPriortyToggle} />)}
                                        {item.priority === 2 && (<Dropdown.Toggle as={MediumPriortyToggle} />)}
                                        {item.priority === 3 && (<Dropdown.Toggle as={HighPriortyToggle} />)}

                                        <Dropdown.Menu className="DropDownMenuBoxCus">
                                          <Dropdown.Item className="DropDListWIcon" onClick={(e) => { e.stopPropagation(); changePriority(item, 1); }}>Low</Dropdown.Item>
                                          <Dropdown.Item className="DropDListWIcon" onClick={(e) => { e.stopPropagation(); changePriority(item, 2); }}>Medium</Dropdown.Item>
                                          <Dropdown.Item className="DropDListWIcon" onClick={(e) => { e.stopPropagation(); changePriority(item, 3); }}>High</Dropdown.Item>
                                        </Dropdown.Menu>
                                      </Dropdown>
                                    </div>
                                  ) : (
                                    <>
                                      {item.priority === 1 && (<p className={`${styles.ReffStatus} ${styles.Low}`}>
                                        <ArrowUp />
                                        <span>Low</span>
                                      </p>)}
                                      {item.priority === 2 && (<p className={`${styles.ReffStatus} ${styles.Medium}`}>
                                        <ArrowUp />
                                        <span>Medium</span>
                                      </p>)}
                                      {item.priority === 3 && (<p className={`${styles.ReffStatus} ${styles.High}`}>
                                        <ArrowUp />
                                        <span>High</span>
                                      </p>)}
                                    </>
                                  )}
                                </td>
                                <td>
                                  <div className={`${styles.MainReqConDropBU}`} onClick={(e) => e.stopPropagation()}>
                                    {item.is_archived === 1 && <span className={`${styles.ArchivedBadge}`}>Archived</span>}
                                    {!(item.is_archived === 1) && !(isAuthenticated && user["https://propertydek.com/permissions"].indexOf("request:update") > -1) && (<Dropdown drop={dropdownDrop}>
                                      {item.current_status === 0 && (<Dropdown.Toggle as={OpenedToggleReadOnly} />)}
                                      {item.current_status === 1 && (<Dropdown.Toggle as={AssignedToggleReadOnly} />)}
                                      {item.current_status === 2 && item?.is_schedule_overdue === 1 && (<Dropdown.Toggle as={ScheduledOverdueToggleReadOnly} />)}
                                      {item.current_status === 2 && item?.is_schedule_overdue === 0 && (<Dropdown.Toggle as={ScheduledToggleReadOnly} />)}
                                      {item.current_status === 5 && (<Dropdown.Toggle as={PendingToggleReadOnly} />)}
                                      {item.current_status === 3 && (<Dropdown.Toggle as={ResolvedToggleReadOnly} />)}
                                      {item.current_status === 4 && (<Dropdown.Toggle as={CancelledToggleReadOnly} />)}
                                    </Dropdown>)}

                                    {!(item.is_archived === 1) && isAuthenticated && user["https://propertydek.com/permissions"].indexOf("request:update") > -1 && (<Dropdown drop={dropdownDrop}>
                                      {item.current_status === 0 && (<Dropdown.Toggle as={OpenedToggle} />)}
                                      {item.current_status === 1 && (<Dropdown.Toggle as={AssignedToggle} />)}
                                      {item.current_status === 2 && item?.is_schedule_overdue === 1 && (<Dropdown.Toggle as={ScheduledOverdueToggle} />)}
                                      {item.current_status === 2 && item?.is_schedule_overdue === 0 && (<Dropdown.Toggle as={ScheduledToggle} />)}
                                      {item.current_status === 5 && (<Dropdown.Toggle as={PendingToggle} />)}
                                      {item.current_status === 3 && (<Dropdown.Toggle as={ResolvedToggle} />)}
                                      {item.current_status === 4 && (<Dropdown.Toggle as={CancelledToggle} />)}

                                      <Dropdown.Menu className="DropDownMenuBoxCus">
                                        <Dropdown.Item className="DropDListWIcon" onClick={(e) => { e.stopPropagation(); changeStatus(item, 0); }}>Open</Dropdown.Item>
                                        <Dropdown.Item className="DropDListWIcon" onClick={(e) => { e.stopPropagation(); changeStatus(item, 1); }}>Assigned</Dropdown.Item>
                                        <Dropdown.Item className="DropDListWIcon" onClick={(e) => { e.stopPropagation(); changeStatus(item, 2); }}>Scheduled</Dropdown.Item>
                                        <Dropdown.Item className="DropDListWIcon" onClick={(e) => { e.stopPropagation(); changeStatus(item, 5); }}>Pending Follow-up</Dropdown.Item>
                                        <Dropdown.Item className="DropDListWIcon" onClick={(e) => { e.stopPropagation(); changeStatus(item, 3); }}>Resolved</Dropdown.Item>
                                        <Dropdown.Item className="DropDListWIcon" onClick={(e) => { e.stopPropagation(); changeStatus(item, 4); }}>Hold</Dropdown.Item>
                                      </Dropdown.Menu>
                                    </Dropdown>)}
                                  </div>
                                </td>
                                <td>
                                  <span className={`${styles.reqCreated}`}>{dayjs(item?.created_at).fromNow()}</span>
                                </td>
                              </tr>
                            );
                          })}

                          {/* Sentinel row: entering the viewport triggers loading the next chunk */}
                          <tr ref={sentinelRef} style={{ height: 1 }}>
                            <td colSpan={7} style={{ padding: 0, border: 'none' }} />
                          </tr>

                          {loadingMore && (
                            <>
                              <tr className={styles.skeletonRow}>
                                <td>
                                  <div className={styles.reqCell}>
                                    <div className={`${styles.skeletonBox} ${styles.skeletonThumb}`} />
                                    <div style={{ flex: 1 }}>
                                      <div className={`${styles.skeletonBox} ${styles.skeletonTitle}`} />
                                      <div className={`${styles.skeletonBox} ${styles.skeletonSub}`} />
                                    </div>
                                  </div>
                                </td>
                                <td><div className={`${styles.skeletonBox} ${styles.skeletonText}`} /></td>
                                <td><div className={`${styles.skeletonBox} ${styles.skeletonText}`} /></td>
                                <td><div className={`${styles.skeletonBox} ${styles.skeletonText}`} /></td>
                                <td><div className={`${styles.skeletonBox} ${styles.skeletonPill}`} /></td>
                                <td><div className={`${styles.skeletonBox} ${styles.skeletonPill}`} /></td>
                                <td><div className={`${styles.skeletonBox} ${styles.skeletonText}`} /></td>
                              </tr>
                              <tr className={styles.scrollLoadingRow}>
                                <td colSpan={7}>
                                  <div className={styles.scrollLoaderWrap}>
                                    <div className={styles.scrollSpinner} />
                                    <span>Loading more requests...</span>
                                  </div>
                                </td>
                              </tr>
                            </>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {items.length === 0 && !contentLoading && (<div className={`${styles.NoDataMain}`}>
                    <div className={`${styles.NoDataIMG}`}>
                      <img src="/static/img/no-request.png" alt="No Data" />
                    </div>
                    <p className={`${styles.NoDataText}`}>All caught up!</p>
                    <p className={`${styles.NoDataTextSub}`}>There are currently no requests to show.</p>
                  </div>)}

                </div>

              </div>

            </div>

            <Dialog
              open={addModalOpen}
              fullWidth
              maxWidth="sm"
              onClose={(e) => setAddModalOpen(false)}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
              className="NewPopUp MobileForm"
            >
              <DialogTitle id="alert-dialog-title">
                <button className={`${styles.BackAddBU}`} onClick={(e) => setAddModalOpen(false)}><ArrowLeft /></button>
                New Maintenance Request
                <button onClick={(e) => setAddModalOpen(false)}><X /></button>
              </DialogTitle>
              <DialogContent className={`${styles.RequestBodyPop}`}>
                <AddMaintenanceForm setAddModalOpen={setAddModalOpen.bind(this)} setLoading={setLoading.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} setSnackbarMsg={setSnackbarMsg.bind(this)} onSubmitLocal={onSubmitLocal.bind(this)} tenant_id={null} />
              </DialogContent>
            </Dialog>
          </div>)}

        </div>
      </div>
    </div>

    <Snackbar
      open={snackbarOpen}
      onClose={(e) => setSnackbarOpen(false)}
      TransitionComponent={Slide}
      message={snackbarMsg}
      autoHideDuration={10000}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      action={
        <IconButton size="small" aria-label="close" color="inherit" onClick={(e) => setSnackbarOpen(false)}>
          <X />
        </IconButton>
      }
    />

    <Footer />
  </React.Fragment>);
};

export default withCustomAuthenticationRequired(MaintenanceRequest, {
  onRedirecting: () => <Loader />,
});